import { LinkProps } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import { NoData } from "design-system/molecules/NoData"
import { cn } from "design-system/utils"
import { AnimatePresence, motion } from "framer-motion"
import { Pin } from "lucide-react"
import { useCanonicalTopicsGroupedQuery } from "modules/dashboard/hooks/useCanonicalTopicsGroupedQuery"
import { useTreemapConfig } from "modules/dashboard/hooks/useTreemapConfig"
import { generateTreemapData } from "modules/dashboard/lib/generateTreemapData"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import ShinyText from "modules/shared/components/Miscelanea/ShinyText"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import React, { Dispatch, SetStateAction, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import {
  CanonicalTopicFilterBy,
  CanonicalTopicGroupedBy,
  CanonicalTopicGroupedByEnum,
  CanonicalTopicsGroupedDataSchema,
  ClusterDomain,
  ClusterSegmentEnum,
  GoalType,
  TimeFrame,
} from "schema"
import { z } from "zod"
import { TooltipData, TooltipOverlay } from "./TooltipOverlay"
import { TopicSidePanel } from "./TopicSidePanel"
import { NodePayload, TreemapChart } from "./TreemapChart"

type Filters = {
  groupedBy: CanonicalTopicGroupedBy
  campaignId?: string | null
  segmentId?: string | null
  goalId?: string | null
}

type Props = {
  domain: ClusterDomain
  groupedBy: CanonicalTopicGroupedBy
  tagBySentiment?: boolean
  goalId?: string | null
  goalType?: GoalType
  goalLabel?: string
  timeFrame: TimeFrame
  filterBy?: CanonicalTopicFilterBy
  groupNonCategoryTopics?: boolean
  setFilters?: Dispatch<SetStateAction<Filters>>
  title?: string | React.ReactNode
  description?: string
  onNodeClick?: (node: NodePayload) => void
  data?: z.infer<typeof CanonicalTopicsGroupedDataSchema>["groups"]
  isPending?: boolean
  onSidePanelOpen?: () => void
  otherSidePanelOpened?: boolean
  onFiltersChange?: (partial: { filterBy?: CanonicalTopicFilterBy }) => void
}

type SelectedNode = {
  tooltip: TooltipData
  navigateTo: LinkProps | undefined
}

const MAX_GROUPS_TO_SHOW = 3

export const GroupedTreemap = ({
  domain,
  timeFrame,
  groupedBy: groupedByProp,
  tagBySentiment = true,
  goalId,
  goalType,
  goalLabel,
  filterBy,
  groupNonCategoryTopics = false,
  setFilters,
  title,
  description,
  onNodeClick,
  data: dataProp,
  isPending: isPendingProp,
  onSidePanelOpen,
  otherSidePanelOpened,
  onFiltersChange,
}: Props) => {
  const { t } = useTranslation()

  const config = useTreemapConfig(domain, goalId ?? null, tagBySentiment)
  const hideTimerRef = React.useRef<number | null>(null)

  const [tooltip, setTooltip] = React.useState<TooltipData | null>(null)
  const lastTooltipRef = React.useRef<TooltipData | null>(null)
  const [hoveredLabel, setHoveredLabel] = React.useState<string | null>(null)
  const [elementsExpanded, setElementsExpanded] = React.useState(false)
  const [isTooltipHovered, setIsTooltipHovered] = React.useState(false)

  const [selectedNode, setSelectedNode] = React.useState<SelectedNode | null>(null)

  React.useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    if (otherSidePanelOpened) {
      setSelectedNode(null)
    }
  }, [otherSidePanelOpened])

  const { data: queryData, isPending: isQueryPending } = useCanonicalTopicsGroupedQuery({
    query: {
      domain: config.domain,
      goalId: config.goalId,
      groupedBy: groupedByProp,
      timeFrame,
      filterBy,
      groupNonCategoryTopics,
    },
    enabled: !dataProp,
  })

  const { data: queryDataGroups, groupedBy: groupedByData } = queryData ?? {}
  const data = dataProp ?? queryDataGroups
  const isPending = isPendingProp ?? isQueryPending
  const isGenerating = queryData?.isGenerating ?? false
  const groupedBy = groupedByData ?? groupedByProp

  const { data: segmentGroups } = useSegmentGroupsQuery()
  const currentSegmentGroup = useMemo(() => {
    if (groupedBy.type !== CanonicalTopicGroupedByEnum.segmentGroup) return undefined
    return segmentGroups?.find((sg) => sg.id === groupedBy.segmentGroupId)
  }, [groupedBy, segmentGroups])

  const noData =
    (isPending && !data) ||
    (data && (data.length === 0 || data.every((d) => d.canonicalTopics.length === 0)))

  const sliceCount = useMemo(() => {
    if (data?.length === 1) return 10
    else if (data?.length === 2) return 8
    return 6
  }, [data?.length])

  const noDataGroups = useMemo(() => {
    if (!noData) return []

    return [
      {
        id: Math.random().toString(),
        name: "",
        label: "",
        data: generateTreemapData(sliceCount),
        flex: 1,
        groupedBy,
        timeFrame,
        rank: 0,
      },
    ]
  }, [groupedBy, noData, sliceCount, timeFrame])

  const dataGroups = useMemo(() => {
    if (noData) return []

    const sumItems = (arr: NonNullable<typeof data>[number]["canonicalTopics"]) =>
      arr.reduce((acc, it) => acc + (it.totalItems ?? 0), 0) ?? 0

    const grandTotal = data?.reduce((acc, group) => acc + sumItems(group.canonicalTopics), 0) || 1

    const genData = (arr: NonNullable<typeof data>[number]["canonicalTopics"]) => {
      if (!arr) return []

      const limited = arr.slice(0, 20)

      return limited.map((item) => ({
        id: item.id,
        label: formatCanonicalTopicLabel(item.label),
        description: item.description,
        customersCount: item.customersCount ?? null,
        conversationsCount: item.conversationsCount ?? null,
        totalItems: item.totalItems,
        snapshotCount: item.snapshotCount,
        snapshotLabels: item.snapshotLabels ?? null,
        snapshotIds: item.snapshotIds ?? null,
        avgSentiment: item.avgSentiment ?? null,
        avgSolveRate: item.avgSolveRate ?? null,
        avgPotentialSolveRate: item.avgPotentialSolveRate ?? null,
        isNewTopic: item.isNewTopic,
        streakInWeeks: item.streakInWeeks,
        rings: config.getRingsFromNode?.(item),
      }))
    }

    return (
      data
        ?.map((group) => {
          const groupTotal = sumItems(group.canonicalTopics)

          return {
            id: group.groupId.toString(),
            name: group.groupName,
            label: config.getGroupLabel(group.groupName, currentSegmentGroup),
            color: config.getGroupColor(group.groupName, currentSegmentGroup?.name),
            data: genData(group.canonicalTopics),
            flex: groupTotal > 0 ? groupTotal / grandTotal : 0,
            timeFrame,
          }
        })
        .filter(
          (g) => groupedBy.type === CanonicalTopicGroupedByEnum.segmentGroup || g.data.length > 0,
        ) ?? []
    )
  }, [noData, data, timeFrame, groupedBy, config, currentSegmentGroup])

  const groups = noData ? noDataGroups : dataGroups

  // Build a memoized map of label -> rank per segment for quick lookup in the tooltip
  const labelToRanks = React.useMemo(() => {
    const map = new Map<string, Record<string, number>>()

    const addRanks = (
      arr: NonNullable<typeof data>[number]["canonicalTopics"],
      segmentId: string,
    ) => {
      arr?.forEach((item, idx) => {
        const label = formatCanonicalTopicLabel(item.label)
        if (!label) return
        const current = map.get(label) ?? {}
        current[segmentId] = idx + 1
        map.set(label, current)
      })
    }

    if (!noData) {
      addRanks(
        data?.find((d) => d.groupName === ClusterSegmentEnum.advocates)?.canonicalTopics ?? [],
        "advocates",
      )
      addRanks(
        data?.find((d) => d.groupName === ClusterSegmentEnum.neutrals)?.canonicalTopics ?? [],
        "neutrals",
      )
      addRanks(
        data?.find((d) => d.groupName === ClusterSegmentEnum.detractors)?.canonicalTopics ?? [],
        "detractors",
      )
    }

    return map
  }, [data, noData])

  const getRanksForLabel = React.useCallback(
    (label?: string) => {
      if (!label) return null
      return labelToRanks.get(label) ?? null
    },
    [labelToRanks],
  )

  const scoreComponent = useMemo(
    () => (tooltip ? config.getScoreComponent(tooltip.node) : null),
    [config, tooltip],
  )

  const showViewAll = dataGroups && dataGroups.length > MAX_GROUPS_TO_SHOW
  const hideMoreElements = showViewAll && !elementsExpanded

  const displayedGroups = useMemo(() => {
    const visibleGroups = hideMoreElements ? groups.slice(0, MAX_GROUPS_TO_SHOW) : groups

    const visibleTotal = visibleGroups.reduce((acc, group) => acc + group.flex, 0)

    return visibleGroups.map((group) => ({
      ...group,
      flex: visibleTotal > 0 ? group.flex / visibleTotal : 1 / visibleGroups.length,
    }))
  }, [groups, hideMoreElements])

  // NOTE: This prevents having "normalized" elements with the noData empty state
  useEffect(() => {
    if (noData && !hideMoreElements) {
      setElementsExpanded(false)
    }
  }, [noData, hideMoreElements])

  return (
    <Card className="p-6 flex flex-col flex-1 min-w-0 h-full min-h-0">
      <div className="flex flex-col w-full gap-4 overflow-hidden h-full">
        {(showViewAll || title || description) && (
          <div className="flex flex-row gap-8">
            <div className="flex flex-col justify-between gap-0.5 items-start z-40 w-full">
              <div className="flex flex-row gap-1 items-center justify-between w-full">
                {typeof title === "string" ? (
                  <span className="text-lg font-medium">{title}</span>
                ) : (
                  title
                )}
                {showViewAll && (
                  <div className="flex flex-row gap-2 items-center shrink-0">
                    <Label className="text-sm text-muted-foreground" htmlFor={`expand-${domain}`}>
                      {t("shared.viewAll")}
                    </Label>
                    <Switch
                      checked={elementsExpanded}
                      onCheckedChange={setElementsExpanded}
                      id={`expand-${domain}`}
                    />
                  </div>
                )}
              </div>
              {description && <span className="text-sm text-muted-foreground">{description}</span>}
            </div>
            {isGenerating && (
              <div className="flex items-end justify-end w-fit text-nowrap">
                <ShinyText text={t("insights.openGoal.treemap.generatingData")} />
              </div>
            )}
          </div>
        )}
        <motion.div
          layout
          className={cn(
            "gap-3 flex flex-row items-stretch flex-1 min-h-0",
            elementsExpanded && "grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3",
            groupedBy.type === CanonicalTopicGroupedByEnum.company &&
              "grid grid-cols-1 md:grid-cols-1 2xl: 2xl:grid-cols-1",
          )}
        >
          <AnimatePresence mode="popLayout">
            {displayedGroups.map((g) => {
              const segmentId =
                groupedBy.type === CanonicalTopicGroupedByEnum.segmentGroup ? g.id : undefined

              const campaignId =
                groupedBy.type === CanonicalTopicGroupedByEnum.campaign ? g.id : undefined

              const onPress =
                groupedBy.type !== CanonicalTopicGroupedByEnum.company && displayedGroups.length > 1
                  ? () => {
                      setFilters?.((filters) => ({
                        ...filters,
                        campaignId: campaignId ?? filters.campaignId,
                        segmentId: segmentId ?? filters.segmentId,
                      }))
                    }
                  : null

              const label = (
                <span
                  className="text-sm font-normal text-foreground line-clamp-1 truncate group-focus-visible:underline"
                  title={g.label}
                >
                  {g.label}
                </span>
              )

              const header =
                setFilters && onPress ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="link"
                        size="sm"
                        className="group p-0 h-auto group flex flex-row gap-2 items-center justify-between outline-0! ring-0! ring-transparent!"
                        onClick={onPress}
                      >
                        {label}
                        <Pin className="size-2.5 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity transition-duration-200" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t("dashboard.tooltip.applyFilters")}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  label
                )

              return (
                <motion.div
                  key={`${groupedBy}-${g.id}`}
                  className="h-full flex flex-1 min-w-20 flex-col gap-2 min-h-0"
                  style={{ flex: g.flex }}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {groupedBy.type !== CanonicalTopicGroupedByEnum.company && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="flex flex-col gap-0"
                    >
                      {header}
                      {!noData && (
                        <div
                          className={cn("h-1.5 rounded-xs")}
                          style={{
                            backgroundColor:
                              config.getGroupColor(g.name, currentSegmentGroup?.name) ??
                              "bg-neutral",
                          }}
                        />
                      )}
                    </motion.div>
                  )}
                  <motion.div
                    className="flex-1 h-full min-h-0"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    {g.data.length > 0 ? (
                      <TreemapChart
                        key={`${config.domain}-${groupedBy}-${g.id}`}
                        data={g.data}
                        noData={noData}
                        segment={g.id}
                        colorBy={config.colorBy}
                        hoveredLabel={hoveredLabel}
                        onHoverLabelChange={setHoveredLabel}
                        onTooltipChange={(d) => {
                          if (d == null) {
                            if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
                            hideTimerRef.current = window.setTimeout(() => {
                              // Only hide tooltip if not hovering over it
                              if (!isTooltipHovered) setTooltip(null)
                            }, 120)
                          } else {
                            if (hideTimerRef.current) {
                              window.clearTimeout(hideTimerRef.current)
                              hideTimerRef.current = null
                            }
                            lastTooltipRef.current = d
                            setTooltip(d)
                          }
                        }}
                        onNodeClick={(node) => {
                          if (onNodeClick) {
                            onNodeClick(node)
                            return
                          }

                          const activeTooltip = tooltip ?? lastTooltipRef.current
                          if (!activeTooltip) return

                          const navigateTo = config.getNavigateTo?.({
                            node,
                            domain,
                            segmentId,
                            canonicalId: node.id?.toString() ?? "",
                            campaignId,
                          })

                          if (hideTimerRef.current) {
                            window.clearTimeout(hideTimerRef.current)
                            hideTimerRef.current = null
                          }
                          setTooltip(null)
                          onSidePanelOpen?.()
                          setSelectedNode({ tooltip: activeTooltip, navigateTo })
                        }}
                      />
                    ) : (
                      <div className="h-full min-h-[280px] flex flex-col items-center justify-center gap-2 text-center bg-muted/50 border-muted-foreground/5 border rounded p-2 my-0.5">
                        <span className="text-sm font-normal text-muted-foreground group-focus-visible:underline">
                          {t("dashboard.noDataToDisplay")}
                        </span>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )
            })}
          </AnimatePresence>
          {hideMoreElements && (
            <span
              className="text-center text-xs text-muted-foreground"
              style={{ writingMode: "sideways-lr" }}
            >
              {t("dashboard.moreElements", { count: groups.length - MAX_GROUPS_TO_SHOW })}
            </span>
          )}
        </motion.div>
        {noData && !isPending && (
          <NoData title={config.noDataTitle} description={config.noDataDescription} />
        )}
      </div>
      {typeof document !== "undefined" && tooltip && !selectedNode
        ? createPortal(
            <TooltipOverlay
              key={config.domain}
              domain={config.domain}
              tooltip={tooltip}
              scoreComponent={scoreComponent}
              getRanksForLabel={getRanksForLabel}
              onMouseEnter={() => {
                setIsTooltipHovered(true)
                if (hideTimerRef.current) {
                  window.clearTimeout(hideTimerRef.current)
                  hideTimerRef.current = null
                }
              }}
              onMouseLeave={() => {
                setIsTooltipHovered(false)
                // Set a small delay before hiding when leaving tooltip
                if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
                hideTimerRef.current = window.setTimeout(() => setTooltip(null), 120)
              }}
            />,
            document.body,
          )
        : null}
      {selectedNode && (
        <TopicSidePanel
          domain={config.domain}
          goalType={goalType}
          goalLabel={goalLabel}
          tooltip={selectedNode.tooltip}
          navigateTo={selectedNode.navigateTo}
          filterBy={filterBy}
          onFiltersChange={onFiltersChange}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </Card>
  )
}
